import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Chip, Typography, Box, Badge } from '@material-ui/core';
import auditDataStore from '../../stores/license/auditDataStore';

const LicenseFormSelector: React.FC = observer(() => {
    const hasGrantedForms = auditDataStore.availableGrantedForms.length > 0;
    const hasOwnForms = (auditDataStore.auditStats?.licenseManagerForms?.length || 0) > 0;
    
    // Ensure we're on the "own" form type when component loads if no granted forms
    useEffect(() => {
        if (!hasGrantedForms && hasOwnForms && auditDataStore.currentFormType !== 'own') {
            auditDataStore.setFormType('own');
        }
    }, [hasGrantedForms, hasOwnForms]);

    const handleChipClick = (type: 'own' | 'granted', grantedIndex?: number) => {
        if (type === 'own') {
            auditDataStore.setFormType('own');
        } else {
            auditDataStore.setFormType('granted', grantedIndex);
        }
    };

    const hasUnsavedChanges = (formType: 'own' | 'granted', index?: number): boolean => {
        if (!auditDataStore.selectedAudit || !auditDataStore.auditStats) return false;
        
        const cacheKey = formType === 'own' 
            ? `${auditDataStore.selectedAudit.id}_own`
            : `${auditDataStore.selectedAudit.id}_granted_${index}`;
            
        const cachedForm = auditDataStore.formCache[auditDataStore.selectedAudit.id]?.[cacheKey];
        if (!cachedForm) return false;
        
        // Get the original form from auditStats to compare
        let originalForm;
        if (formType === 'own' && auditDataStore.auditStats.licenseManagerForms?.length > 0) {
            originalForm = auditDataStore.auditStats.licenseManagerForms[0];
        } else if (formType === 'granted' && auditDataStore.auditStats.grantedLicenseManagerForms?.length > (index || 0)) {
            originalForm = auditDataStore.auditStats.grantedLicenseManagerForms[index || 0];
        }
        
        if (!originalForm) return false;

        if (cachedForm.comment !== originalForm.comment) return true;

        if (cachedForm.licenseReviews.length !== originalForm.licenseReviews.length) return true;
        
        for (let i = 0; i < cachedForm.licenseReviews.length; i++) {
            const cachedReview = cachedForm.licenseReviews[i];
            const originalReview = originalForm.licenseReviews[i];
            
            if (cachedReview.licenseReviewActions.length !== originalReview.licenseReviewActions.length) return true;
            
            for (let j = 0; j < cachedReview.licenseReviewActions.length; j++) {
                const cachedAction = cachedReview.licenseReviewActions[j];
                const originalAction = originalReview.licenseReviewActions[j];

                if (cachedAction.selectedOptions.length !== originalAction.selectedOptions.length) return true;
                if (!cachedAction.selectedOptions.every(option => originalAction.selectedOptions.includes(option))) return true;
            }
        }
        
        return false;
    };

    const getManagerName = () => {
        return auditDataStore.currentFormType === 'own' && auditDataStore.currentFormState?.managerName 
            ? auditDataStore.currentFormState.managerName
            : auditDataStore.auditStats?.licenseManagerForms?.[0]?.managerName || 'My Forms';
    };

    return (
        <Box mb={2} display="flex" flexDirection="column" alignItems="center" className="license-form-selector-container">
            <Box display="flex" justifyContent="center" flexWrap="wrap">
                <Badge 
                    invisible={!hasUnsavedChanges('own') || auditDataStore.selectedAudit?.isCompleted}
                    badgeContent="•"
                    color="secondary"
                    className="chip-badge"
                    style={{ margin: '4px' }}
                >
                    <Chip
                        label={getManagerName()}
                        onClick={() => handleChipClick('own')}
                        variant={auditDataStore.currentFormType === 'own' ? 'default' : 'outlined'}
                        color={auditDataStore.currentFormType === 'own' ? 'primary' : 'default'}
                        clickable={true}
                        className={`manager-chip ${auditDataStore.selectedAudit?.isCompleted ? 'preview-mode' : ''}`}
                    />
                </Badge>
                
                {auditDataStore.availableGrantedForms.map((form, index) => (
                    <Badge 
                        key={form.email}
                        invisible={!hasUnsavedChanges('granted', index) || auditDataStore.selectedAudit?.isCompleted}
                        badgeContent="•"
                        color="secondary"
                        className="chip-badge"
                        style={{ margin: '4px' }}
                    >
                        <Chip
                            label={form.name}
                            onClick={() => handleChipClick('granted', index)}
                            variant={auditDataStore.currentFormType === 'granted' && auditDataStore.selectedGrantedFormIndex === index ? 'default' : 'outlined'}
                            color={auditDataStore.currentFormType === 'granted' && auditDataStore.selectedGrantedFormIndex === index ? 'primary' : 'default'}
                            clickable={true}
                            className={`manager-chip ${auditDataStore.selectedAudit?.isCompleted ? 'preview-mode' : ''}`}
                        />
                    </Badge>
                ))}
            </Box>
            
            {!auditDataStore.selectedAudit?.isCompleted && hasUnsavedChanges(auditDataStore.currentFormType, auditDataStore.currentFormType === 'granted' ? auditDataStore.selectedGrantedFormIndex : undefined) && (
                <Box display="flex" justifyContent="center" mt={1}>
                    <Typography variant="caption" style={{ color: 'orange' }}>
                        You have unsaved changes in this form
                    </Typography>
                </Box>
            )}
            
            {auditDataStore.selectedAudit?.isCompleted && (
                <Box display="flex" justifyContent="center" mt={1}>
                    <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                        Viewing completed audit - forms are read-only
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

export default LicenseFormSelector; 